import React, {useCallback, useEffect, useState} from "react";
import {
    Alert,
    ConfirmModal,
    Form,
    InputLabel,
    PasswordStrengthBar,
    showPasswordAddon,
    TextField,
    useNotifications,
} from "peggirkit";
import {FormattedMessage, useIntl} from "react-intl";
import {useForm} from "react-hook-form";
import {
    passwordFieldPlaceholder,
    passwordFieldType,
    passwordMinLength,
    passwordScoreWords,
    passwordValidationLengths,
    togglePasswordVisibilityText
} from "@/utils/PasswordUtils";
import {LockClosedIcon} from "@heroicons/react/24/outline";
import {notifySuccess} from "@/utils/NotifyUtils";
import {api} from "@/data/api";

type ChangePasswordForm = {
    currentPassword: string,
    newPassword: string,
    passwordRepeated: string,
};

type Props = {
    show: boolean,
    onClose: () => void
};

const ChangePasswordModal = ({show, onClose}: Props) => {
    const intl = useIntl();
    const {
        register,
        getValues,
        handleSubmit,
        watch,
        reset,
        setFocus,
        formState: {errors}
    } = useForm<ChangePasswordForm>();
    const [loading, setLoading] = useState(false);
    const [formError, setFormError] = useState<string | null>(null);
    const [showCurrentPwd, setShowCurrentPwd] = useState(false);
    const [showNewPwds, setShowNewPwds] = useState(false);
    const {notify} = useNotifications();
    const closeHandler = useCallback(() => {
        onClose();
        setFormError(null);
        setShowCurrentPwd(false);
        setShowNewPwds(false);
        reset();
    }, [onClose, setFormError, setShowCurrentPwd, setShowNewPwds, reset]);

    // Submit change password // TODO - Should be done with Tanstack
    useEffect(() => {
        if (!loading)
            return;

        const {currentPassword, newPassword}: ChangePasswordForm = getValues();
        setFormError(null);
        api.user.updatePassword(currentPassword, newPassword)
            .then(() => {
                setLoading(false);
                closeHandler();
                notifySuccess(intl.formatMessage({id: "passwordUpdated"}), notify, LockClosedIcon);
            })
            .catch(e => {
                setFormError(intl.formatMessage({
                    id: e.response.status === 400
                        ? "incorrectCurrentPassword"
                        : "errorUpdatingPassword"
                }));

                setLoading(false)
            });
    }, [loading, closeHandler, getValues, intl, notify]);

    return (
        <ConfirmModal
            title={intl.formatMessage({id: "changePassword"})}
            type={"info"}
            loading={loading}
            show={show}
            dismissButton={{text: intl.formatMessage({id: "cancel"})}}
            onClose={closeHandler}
            continueButton={{
                text: intl.formatMessage({id: "save"}),
                onClick: () => {
                    handleSubmit(() => setLoading(true))();
                }
            }}
            transitionEvents={{
                afterEnter: () => {
                    setFocus("currentPassword")
                }
            }}
            position={"center-bottom"}
            breakpoints={{md: {position: "center"}}}
        >
            <p>
                <FormattedMessage id={"enterFieldsToUpdatePassword"}/>
            </p>

            <Form
                containerClassname={"my-6"}
                onSubmit={handleSubmit(() => setLoading(true))}
                alert={
                    formError
                        ? <Alert
                            containerClassname={"-mb-4"}
                            type={"danger"}
                            title={formError}
                        />
                        : undefined
                }
            >
                <TextField
                    type={passwordFieldType(showCurrentPwd)}
                    placeholder={passwordFieldPlaceholder(showCurrentPwd)}
                    label={<InputLabel><FormattedMessage id={"currentPassword"}/></InputLabel>}
                    disabled={loading}
                    trailing={
                        showPasswordAddon(showCurrentPwd, setShowCurrentPwd, s => togglePasswordVisibilityText(s, intl))
                    }
                    error={errors.currentPassword && errors.currentPassword.message}
                    {
                        ...register("currentPassword", {
                            required: intl.formatMessage({id: "enterAPassword"}),
                            ...passwordValidationLengths(intl),
                        })
                    }
                />

                <TextField
                    type={passwordFieldType(showNewPwds)}
                    placeholder={passwordFieldPlaceholder(showNewPwds)}
                    label={<InputLabel><FormattedMessage id={"newPassword"}/></InputLabel>}
                    disabled={loading}
                    paddingRight={0}
                    trailing={
                        showPasswordAddon(showNewPwds, setShowNewPwds, s => togglePasswordVisibilityText(s, intl))
                    }
                    after={
                        <PasswordStrengthBar
                            password={watch("newPassword") || ""}
                            minLength={passwordMinLength}
                            scoreWords={passwordScoreWords(intl)}
                        />
                    }
                    error={errors.newPassword && errors.newPassword.message}
                    {
                        ...register("newPassword", {
                            required: intl.formatMessage({id: "enterAPassword"}),
                            ...passwordValidationLengths(intl),
                        })
                    }
                />

                <TextField
                    type={passwordFieldType(showNewPwds)}
                    placeholder={passwordFieldPlaceholder(showNewPwds)}
                    label={<InputLabel><FormattedMessage id={"repeatPassword"}/></InputLabel>}
                    disabled={loading}
                    error={errors.passwordRepeated && errors.passwordRepeated.message}
                    {
                        ...register("passwordRepeated", {
                            required: intl.formatMessage({id: "repeatYourPassword"}),
                            ...passwordValidationLengths(intl),
                            validate: (repeatedPassword: string) => repeatedPassword === watch("newPassword")
                                ? true
                                : intl.formatMessage({id: "passwordsMustMatch"}),
                        })
                    }
                />
            </Form>
        </ConfirmModal>
    );
};

export default ChangePasswordModal;